import React from 'react';
import { Link } from 'react-router-dom';
import { FiTrash2, FiMinus, FiPlus, FiShoppingCart, FiArrowRight } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { formatPrice, getImageUrl } from '../utils/helpers';

const CartPage = () => {
  const { cartItems, removeFromCart, updateQty, subtotal, clearCart } = useCart();

  const shipping = subtotal >= 5000 ? 0 : 300;
  const total = subtotal + shipping;

  if (cartItems.length === 0) {
    return (
      <div className="container-custom py-20 text-center">
        <FiShoppingCart size={64} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-6">Add some medical equipment to get started!</p>
        <Link to="/shop" className="btn-primary">Browse Products</Link>
      </div>
    );
  }

  return (
    <div className="container-custom py-8">
      <h1 className="section-title mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart items */}
        <div className="lg:col-span-2 space-y-3">
          {cartItems.map((item) => (
            <div key={item._id} className="card p-4 flex items-center gap-4">
              <img
                src={getImageUrl(item.images?.[0])}
                alt={item.name}
                className="w-20 h-20 object-contain rounded-lg bg-gray-50 flex-shrink-0"
                onError={(e) => { e.target.src = '/placeholder.png'; }}
              />
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.slug || item._id}`} className="font-semibold text-gray-800 hover:text-primary-600 transition-colors line-clamp-2">
                  {item.name}
                </Link>
                <p className="text-primary-600 font-bold mt-1">{formatPrice(item.price)}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => updateQty(item._id, item.qty - 1)} className="w-8 h-8 border border-gray-300 rounded-lg flex items-center justify-center hover:border-primary-500 transition-colors">
                  <FiMinus size={14} />
                </button>
                <span className="w-8 text-center font-semibold">{item.qty}</span>
                <button onClick={() => updateQty(item._id, item.qty + 1)} className="w-8 h-8 border border-gray-300 rounded-lg flex items-center justify-center hover:border-primary-500 transition-colors">
                  <FiPlus size={14} />
                </button>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-gray-800">{formatPrice(item.price * item.qty)}</p>
                <button onClick={() => removeFromCart(item._id)} className="text-red-400 hover:text-red-600 mt-1 transition-colors">
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          <button onClick={clearCart} className="text-sm text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors mt-2">
            <FiTrash2 size={14} /> Clear cart
          </button>
        </div>

        {/* Summary */}
        <div>
          <div className="card p-6 sticky top-24">
            <h2 className="font-bold text-lg mb-5">Order Summary</h2>
            <div className="space-y-3 mb-5">
              <div className="flex justify-between text-gray-600 text-sm">
                <span>Subtotal ({cartItems.reduce((a, i) => a + i.qty, 0)} items)</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600 text-sm">
                <span>Shipping</span>
                <span>{shipping === 0 ? <span className="text-green-600 font-medium">FREE</span> : formatPrice(shipping)}</span>
              </div>
              {shipping > 0 && (
                <p className="text-xs text-gray-400 bg-blue-50 rounded-lg p-2">
                  Add {formatPrice(5000 - subtotal)} more for free shipping!
                </p>
              )}
              <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-primary-600">{formatPrice(total)}</span>
              </div>
            </div>
            <Link to="/checkout" className="btn-primary w-full flex items-center justify-center gap-2">
              Proceed to Checkout <FiArrowRight size={16} />
            </Link>
            <Link to="/shop" className="block text-center text-sm text-gray-500 hover:text-primary-600 mt-3 transition-colors">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
